/* global Lang */

import React, { useState, useRef, useMemo } from 'react';
import styled, { css } from 'styled-components';
import Card, { CardBody, CardTitle } from '../Card';
import Popover from '../Popover';
import DomainHeroScores from '../DomainHeroScores';
import ClickOutside from '../../../common/utilities/ClickOutside';

const ActivityIndex = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  font-weight: 900;
  opacity: 0.1;
  font-size: 10rem;
  line-height: 0.7em;
  text-indent: -0.05em;
  z-index: 0;

  pointer-events: none;
`;

const StyledPopover = styled(Popover)`
  top: 100%;
  margin: 15px;
  max-width: 90vw;
  width: 500px;

  ${p => (p.right
    ? css`
          left: auto;
          right: 0;

          & > .arrow {
            left: auto;
            right: 20px;
          }
        `
    : '')}
`;

const HeroColor = styled.span`
  display: inline-block;
  width: 1em;
  height: 1em;
  background: ${p => p.color};
  border-radius: 100%;
  border: 1px solid black;
  margin: 0 0 0 0.3em;
`;

const PlayerActivity = ({
  className = '',
  index,
  domains: domainPivots,
  fullDomains,
  question,
  counters: { selected, asked, percentage },
}) => {
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [detailsRight, setDetailsRight] = useState(false);

  const domains = useMemo(
    () => domainPivots.map((pivot) => {
      for (let i = 0; i < fullDomains.length; i += 1) {
        const domain = fullDomains[i];
        if (domain.id === pivot.id) {
          return {
            ...pivot,
            ...domain,
          };
        }
      }
      return pivot;
    }),
    [domainPivots, fullDomains],
  );
  const heroDomain = domains.find(d => !!d.hero);
  const riasocDomain = domains.find(d => !d.hero);

  const element = useRef();
  const toggleDetails = () => {
    if (!detailsVisible) {
      // determine the position of the popover
      setDetailsRight(element.current.getBoundingClientRect().left > window.innerWidth * 0.5);
    }
    setDetailsVisible(!detailsVisible);
  };

  return (
    <div ref={element} className={`${className} col mb-4`}>
      <Card className="h-100">
        <ActivityIndex>{index}</ActivityIndex>
        <CardBody>
          <CardTitle>
            <HeroColor color={heroDomain.hero.color} className="float-right" />
            {question}
          </CardTitle>
          <h6 color={heroDomain.hero.color}>{`${heroDomain.title} (${heroDomain.hero.name})`}</h6>
          <h6>{riasocDomain.title}</h6>
          <ClickOutside
            onClickOutside={() => {
              setDetailsVisible(false);
            }}
          >
            <button className="btn btn-link p-0" type="button" onClick={() => toggleDetails()}>
              {Lang.get('messages.dashboard.activities.showDetails')}
              <i className="fas fa-angle-right ml-2" />
            </button>
          </ClickOutside>
        </CardBody>
      </Card>
      <StyledPopover show={detailsVisible} position="bottom" right={detailsRight}>
        <h4 className="text-dark">{question}</h4>
        <div className="row">
          <div className="col-6">
            <h6 color={heroDomain.hero.color}>
              <strong>
                {Lang.get('messages.dashboard.hero')}
:
              </strong>
              {' '}
              {`${heroDomain.title} (${heroDomain.hero.name})`}
              <HeroColor color={heroDomain.hero.color} />
            </h6>
          </div>
          <div className="col-6">
            <h6>
              <strong>
                {Lang.get('messages.dashboard.riasoc')}
:
              </strong>
              {' '}
              {riasocDomain.title}
            </h6>
          </div>
        </div>
        <div className="alert alert-secondary mt-2">
          <div className="row">
            <div className="col">
              <p className="m-0">
                <strong>{`${asked} x `}</strong>
                <span>{Lang.get('messages.dashboard.activities.offered')}</span>
              </p>
            </div>
            <div className="col">
              <p className="m-0">
                <strong>{`${selected} x `}</strong>
                <span>{Lang.get('messages.dashboard.activities.selected')}</span>
              </p>
            </div>
            <div className="col">
              <p className="m-0">
                <strong>{`${Math.floor(percentage * 100)}% `}</strong>
                <span>{Lang.get('messages.dashboard.activities.fitting')}</span>
              </p>
            </div>
          </div>
        </div>
        <h5>{Lang.get('messages.dashboard.activities.whichHeroesFitBest')}</h5>
        <DomainHeroScores domains={domains} />
      </StyledPopover>
    </div>
  );
};

export default styled(PlayerActivity)`
  flex-basis: 50%;

  @media screen and (min-width: 800px) {
    flex-basis: 18%;
  }
`;
